import { useEffect, useMemo, useState, type KeyboardEventHandler } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  FloatingPortal,
  flip,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";

import { renderChevronAnimation } from "./selects/utils";
import { TextInput } from "./text-input";

const DEFAULT_OPERATOR_OPTIONS = [
  { label: "Is", value: "is" },
  { label: "Is not", value: "is_not" },
  { label: "Contains", value: "contains" },
  { label: "Does not contain", value: "does_not_contain" },
  { label: "Starts with", value: "starts_with" },
  { label: "Ends with", value: "ends_with" },
  { label: "Matches Regex", value: "regex" },
];

interface OperatorOption {
  label: string;
  value: string;
}

export interface SearchValue {
  operator: string;
  query: string;
}

interface Props {
  options?: OperatorOption[];
  className?: string;
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  value: SearchValue;
  onChange: (value: SearchValue) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

export const SearchInput = ({
  options,
  value,
  className,
  label,
  placeholder,
  isDisabled,
  onChange,
  onKeyDown,
}: Props) => {
  const [isHover, setIsHover] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  useEffect(() => {
    if (isDisabled) {
      setIsHover(false);
      setIsFocus(false);
    }
  }, [isDisabled]);

  return (
    <div
      className={clsx("nb-input-container", className, {
        "pointer-events-none": isDisabled,
      })}
    >
      {label && (
        <label className={clsx("label", { "text-disabled": isDisabled })}>
          {label}
        </label>
      )}
      <div
        className={clsx("flex items-stretch h-8 bg-white border rounded-md", {
          "border-gray": !(isHover || isFocus),
          "border-cosmic": isHover || isFocus,
          "shadow-[0_0_0_1px_#7081FF]": isFocus,
          "opacity-60": isDisabled,
        })}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <OperatorSelect
          options={options ?? DEFAULT_OPERATOR_OPTIONS}
          value={value.operator}
          onChange={(operator) => {
            onChange({ ...value, operator });
            setIsHover(false);
          }}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
        />
        <div className="text-sm flex-grow">
          <TextInput
            placeholder={placeholder}
            disabled={isDisabled}
            onKeyDown={onKeyDown}
            inputClassName={clsx(
              "border-y-0 border-r-0 h-full w-full min-w-[80px] rounded-l-none rounded-r-md",
              "hover:bg-white focus:ring-0",
              { "border-cosmic": isHover || isFocus },
            )}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
            value={value.query}
            onChange={({ target }) =>
              onChange({ ...value, query: target.value })
            }
          />
        </div>
      </div>
    </div>
  );
};

interface OperatorSelectParams {
  options: OperatorOption[];
  value: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
}

export const OperatorSelect = ({
  options,
  value,
  onChange,
  onFocus,
  onBlur,
}: OperatorSelectParams) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      onFocus();
    } else {
      onBlur();
    }
  }, [isOpen]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "bottom-start",
    middleware: [flip()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const selectedOption = useMemo(() => {
    return options.find((e) => e.value === value);
  }, [options, value]);

  return (
    <div className="shrink-0 flex items-center min-w-[70px]">
      <button
        className={clsx(
          "text-sm flex-1 h-full px-3 flex items-center justify-between gap-2 rounded-l-md",
          { "bg-gray-4": !isOpen, "bg-gray-3": isOpen },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div className="text-left text-interstellar+2">
          {selectedOption?.label}
        </div>
        {renderChevronAnimation(isOpen)}
      </button>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial={{ opacity: 0, translateY: "0.25rem" }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: "0.25rem" }}
              ref={refs.setFloating}
              style={floatingStyles}
              className={clsx(
                "absolute z-[2005]",
                "mt-1 z-10 bg-white rounded-lg elevation-1 text-sm pt-2 pb-2 w-[180px]",
              )}
              {...getFloatingProps()}
            >
              {options.map((option) => (
                <div
                  key={option.value}
                  className={clsx(
                    "px-4 h-9 w-full flex items-center gap-2",
                    "cursor-pointer text-xs hover:bg-cosmic-4",
                    { "font-bold bg-cosmic-4": option.value === value },
                  )}
                  onClick={() => {
                    setIsOpen(false);
                    onChange(option.value);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};
